import { Button } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { FeatureFlagName } from "~/gql/graphqlBase.generated";
import { useAppSelector } from "~/hooks/reduxHooks";
import useFeatureFlag from "~/hooks/useFeatureFlag";
import useLearnParam from "~/hooks/useLearnParam";
import { useSnacks } from "~/providers/SnackProvider";
import { useMyLearningPathQuery } from "~/services/endpoints/apiEndpoints";

const LearningNextConceptPrompt = () => {
	const { t } = useTranslation("common");
	const [openSnack] = useSnacks();
	const { openConcept } = useLearnParam();
	const openConceptRef = useRef(openConcept);
	openConceptRef.current = openConcept;
	const isTourRunning = useAppSelector((state) => state.walkthrough.status === "running");
	const { isEnabled: isLearningPathEnabled } = useFeatureFlag(FeatureFlagName.LearningPath);
	const { data } = useMyLearningPathQuery(undefined, { skip: !isLearningPathEnabled });
	const paths = data?.myLearningPath;
	const completedRef = useRef<Set<string> | null>(null);

	useEffect(() => {
		if (!paths) return;
		const previous = completedRef.current;
		completedRef.current = new Set(
			paths.flatMap((path) => path.concepts.filter((concept) => concept.isComplete).map((concept) => concept.conceptId))
		);
		if (!previous || isTourRunning) return;

		for (const path of paths) {
			const newlyCompletedConcept = path.concepts.find(
				(concept) => concept.isComplete && !previous.has(concept.conceptId)
			);
			if (!newlyCompletedConcept) continue;

			const completedConceptTitle = t(newlyCompletedConcept.titleKey, {
				defaultValue: newlyCompletedConcept.conceptId
			});
			const nextConcept = path.concepts.find((candidate) => candidate.conceptId === path.nextConceptId);
			if (!nextConcept) {
				openSnack(t("learningPath.donePrompt", { concept: completedConceptTitle }), "success");
				return;
			}

			openSnack(
				t("learningPath.nextPrompt", {
					concept: completedConceptTitle,
					next: t(nextConcept.titleKey, { defaultValue: nextConcept.conceptId })
				}),
				"success",
				false,
				() => (
					<Button
						color="inherit"
						onClick={() => openConceptRef.current(nextConcept.conceptId)}
						size="small"
						variant="outlined"
					>
						{t("learningPath.show")}
					</Button>
				)
			);
			return;
		}
	}, [isTourRunning, openSnack, paths, t]);

	return null;
};

export default LearningNextConceptPrompt;
